import { useState, useEffect, useRef } from "react";
import AnswerPill from "./AnswerPill";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
 
import { useDispatch, useSelector } from "react-redux";
import {
  processData,
  fetchResults,
  fetchNextQuestion,
  toggleQuestionPopup,
} from "../../slices/dataSlice";
import Loader from "../loader";
import AlertModal from "../AlertModal/AlertModal";
 
const Question = ({ modalOpen }) => {
  const { currentQuestion, order, isQuestionEdit, newQuestionAPI, processAPI, moreQuestions } =
    useSelector((state) => state.data);
 
  const [textAnswer, setTextAnswer] = useState("");
  const [choicesArray, setChoicesArray] = useState([]);
  const [showAlert, setShowAlert] = useState(false);
 
  const bannedWords = ["badword", "spam", "abuse"];
  const autoSelectTriggeredRef = useRef(false);
 
  const bannedRegex = new RegExp(
    bannedWords
      .map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
      .join("|"),
  );
 
  const dispatch = useDispatch();
  const currentRequestRef = useRef(null);
  const inputRef = useRef(null);
 
 
  useEffect(() => {
    setTextAnswer(currentQuestion?.answer);
    let c = currentQuestion?.choices
      ?.filter((choice) => choice.isSelected === true)
      .map((c) => c.choiceId);
    setChoicesArray(c);
 
    autoSelectTriggeredRef.current = false;
  }, [currentQuestion]);
 
  useEffect(() => {
  if (modalOpen) {
    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 150);
  }
}, [modalOpen, currentQuestion]);
 
 
  const containsBannedWord = (text) => {
    if (!text) return false;
    return bannedRegex.test(text);
  };
 
  const handleAutoSelectChoiceFromText = () => {
    if (
      !textAnswer ||
      !currentQuestion?.choices ||
      autoSelectTriggeredRef.current
    ) {
      return false;
    }
 
    const normalizedText = textAnswer.trim().toLowerCase();
 
    const matchedChoice = currentQuestion.choices.find(
      (choice) => choice.choiceText.trim().toLowerCase() === normalizedText,
    );
 
    if (matchedChoice) {
      setChoicesArray((prev) => {
        if (prev.includes(matchedChoice.choiceId)) return prev;
        return [...prev, matchedChoice.choiceId];
      });
 
      setTextAnswer("");
      autoSelectTriggeredRef.current = true;
 
      setShowAlert(true);
      return true;
    }
 
    return false;
  };
 
  const handleTextChange = (e) => setTextAnswer(e.target.value);
 
  const toggleAnswer = (item) => {
    setChoicesArray((prevChoices) => {
      const exists = prevChoices.includes(item.choiceId);
 
      if (exists) {
        return prevChoices.filter((choice) => choice !== item.choiceId);
      }
 
      return [...prevChoices, item.choiceId];
    });
  };
 
  const handleQuestionSubmit = async (fetchNewQuestion) => {
    let p = await dispatch(
      processData({
        answer: textAnswer,
        isSkipped: false,
        choices: choicesArray,
      }),
    );
 
    if (fetchNewQuestion) {
      if (p.payload) {
        dispatch(fetchNextQuestion());
 
        // If we've answered at least 3 questions, fetch results in the background
        // so the user can continue answering further questions while results load.
        if (order >= 3) {
          if (currentRequestRef.current) {
            currentRequestRef.current.abort();
          }
          const request = dispatch(fetchResults());
          currentRequestRef.current = request;
        }
      }
    }
  };
 
  const showNextQuestion = async () => {
    if (textAnswer.length <= 0) {
      setShowAlert(true);
      return;
    }
    if (handleAutoSelectChoiceFromText()) {
      return;
    }
    if (containsBannedWord(textAnswer)) {
      setShowAlert(true);
      return;
    }
 
    handleQuestionSubmit(true);
 
    /*if (order >= 3) {
      await handleQuestionSubmit(false);
      showResult();
      dispatch(toggleQuestionPopup(false));
    } else {
      handleQuestionSubmit(true);
    }*/
  };
 
  const showResult = async () => {
    if (textAnswer.length <= 0) {
      setShowAlert(true);
      return;
    }
    if (handleAutoSelectChoiceFromText()) {
      return;
    }
    if (containsBannedWord(textAnswer)) {
      setShowAlert(true);
      return;
    }
 
    await handleQuestionSubmit(false);
 
    if (currentRequestRef.current) {
      currentRequestRef.current.abort();
    }
    const request = dispatch(fetchResults());
    currentRequestRef.current = request;
 
    dispatch(toggleQuestionPopup(false));
  };
 
  const handleOnSkipClick = () => {
    if (!isQuestionEdit) {
      dispatch(
        processData({
          answer: textAnswer,
          isSkipped: true,
          choices: choicesArray,
        }),
      );
    }
    dispatch(toggleQuestionPopup(false));
  };
 
  //console.log(order, 'order chekcing');
 
  return (
    <>
      <AlertModal
        open={showAlert}
        message={
          autoSelectTriggeredRef.current
            ? "Your answer matched a choice and was auto-selected."
            : containsBannedWord(textAnswer)
              ? "Your answer contains inappropriate words. Please modify it."
              : "Enter an answer before continuing."
        }
        onClose={() => setShowAlert(false)}
      />
 
      <AnimatePresence>
        {modalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-2xl bg-white rounded-[32px] p-6 sm:p-6 md:p-12 lg:p-12 shadow-2xl overflow-hidden"
            >
              {isQuestionEdit || (order > 3 && moreQuestions) ? (
                <button
                  type="button"
                  onClick={handleOnSkipClick}
                  aria-label="Close and skip question"
                  className="absolute top-6 right-8 text-error-strong hover:scale-110 transition-transform"
                >
                  <X size={28} strokeWidth={3} aria-hidden="true" />
                </button>
              ) : null}
 
              <div className="flex flex-col gap-4 sm:gap-5 md:gap-6 lg:gap-8">
                <h2 className="text-xl sm:text-2xl md:text-3xl font-medium text-text-primary leading-snug md:leading-tight">
                  {currentQuestion?.questionText}
                </h2>
 
                <div className="relative">
                  <label htmlFor="text-answer-input" className="sr-only">
                    Enter your answer
                  </label>
                  <input
                    id="text-answer-input"
                    ref={inputRef}
                    type="text"
                    value={textAnswer}
                    onChange={handleTextChange}
                    placeholder="Enter your answer..."
                    className="w-full text-base sm:text-lg md:text-2xl text-border-extrabold placeholder-border-strong bg-transparent border-none outline-none focus:outline-none focus:ring-0 caret-black p-0"
                  />
                </div>
                <div className="flex flex-wrap gap-2 sm:gap-3 md:gap-4">
                  {currentQuestion?.choices?.map((choice) => (
                    <AnswerPill
                      key={choice.choiceId}
                      text={choice.choiceText}
                      selected={choicesArray?.includes(choice.choiceId)}
                      onToggle={(bool) => toggleAnswer(choice, bool)}
                    />
                  ))}
                </div>
 
                <div className="flex flex-col sm:flex-row sm:justify-end gap-3 sm:gap-4 mt-2 sm:mt-4">
                  {newQuestionAPI.status === "loading" ||
                  processAPI.status === "loading" ? (
                    <Loader />
                  ) : (
                    <>
                      {order >= 3 ? (
                        <button
                          type="button"
                          className="w-full sm:w-auto bg-accent hover:bg-accent-subtle text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-sm sm:text-md font-bold transition-all shadow-lg active:scale-95"
                          onClick={showResult}
                        >
                          See Result
                        </button>
                      ) : null}
 
                      {moreQuestions ? (
                        <button
                          type="button"
                          className="w-full sm:w-auto bg-primary hover:bg-primary-hover text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-sm sm:text-md font-bold transition-all shadow-lg active:scale-95"
                          onClick={showNextQuestion}
                        >
                          Next
                        </button>
                      ) : null}
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};
 
export default Question;